/**
 * Field report G2.1 / G2.2: when Waslo may START a message, and whether it follows up a customer
 * who went quiet in a task. A THIRD spelling of the sidecar's `sending-hours.ts` and
 * `follow-ups/settings.ts` and of the BFF's `config-bag.ts`: the BFF's parity tests hold the two
 * servers together and read the lists below, so this page cannot offer a choice the server refuses.
 * The server is the authority on every rule; the two checks below only catch, in her language,
 * the two mistakes a save would otherwise come back with.
 */
export const MAX_SEASONS = 6;
export const MAX_PAUSES = 12;
export const MAX_SENDING_NAME_CHARS = 60;
export const NUDGE_COUNTS = Object.freeze([1, 2]);
export const FIRST_AFTER_MINUTES = Object.freeze([30, 60, 180]);
export const SECOND_AFTER_MINUTES = Object.freeze([180, 360, 720]);
export const FOLLOW_UP_WORDING = Object.freeze(['question', 'own']);
export const MAX_FOLLOW_UP_CHARS = 300;

/** What an account that never chose reads as: her opening hours, with the Friday prayer pause. */
export const defaultSendingHours = () => ({
  week: null,
  prayerGap: { on: true, from: '11:00', to: '13:30' },
  seasons: [],
  pauses: [],
  broadcasts: true,
});

/** What an account that never chose reads as: off (owner decision D-2). */
export const defaultFollowUps = () => ({
  on: false,
  nudges: 1,
  firstAfterMinutes: 30,
  secondAfterMinutes: 180,
  wording: 'question',
  textEn: null,
  textAr: null,
});

/** Two settings are the same when they say the same: a fresh copy is not a change. */
export const sameSetting = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * The first thing a save of these sending hours would be refused for, as a key under
 * `WASLO.SENDING_HOURS.ERRORS`, or `null`.
 */
export const sendingHoursProblem = value => {
  const gap = value?.prayerGap;
  if (gap?.on && !(gap.from < gap.to)) return 'GAP_ORDER';
  const dated = [...(value?.seasons ?? []), ...(value?.pauses ?? [])];
  const incomplete = item =>
    !item?.name?.trim() || !item.from || !item.to || item.to < item.from;
  return dated.some(incomplete) ? 'DATES' : null;
};

/** The same for follow-ups, under `WASLO.FOLLOW_UPS.ERRORS`. */
export const followUpsProblem = value =>
  value?.on &&
  value.wording === 'own' &&
  !value.textEn?.trim() &&
  !value.textAr?.trim()
    ? 'NEEDS_WORDS'
    : null;
